import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

/** Parse a stat like "15+" -> { num: 15, suffix: "+" }, "Lead" -> { num: null }. */
export function parseStat(value: string): {
  num: number | null
  prefix: string
  suffix: string
} {
  const m = value.match(/^(\D*)(\d+)(\D*)$/)
  if (!m) return { num: null, prefix: '', suffix: value }
  return { num: parseInt(m[2], 10), prefix: m[1], suffix: m[3] }
}

/**
 * Counts from 0 to `target` once `start` is true. Respects reduced motion
 * (jumps straight to the target). Returns the current integer value.
 */
export function useCountUp(
  target: number,
  start: boolean,
  durationMs = 1100,
): number {
  const reduce = useReducedMotion()
  const [value, setValue] = useState(0)
  const raf = useRef<number>()

  useEffect(() => {
    if (!start) return
    if (reduce) {
      setValue(target)
      return
    }
    const t0 = performance.now()
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3)
      setValue(Math.round(eased * target))
      if (p < 1) raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [target, start, durationMs, reduce])

  return value
}
